import DefaultValuesUtils from "./DefaultValuesUtils";

/**
 * Class responsible for dealing with WhatsApp JIDs.
 * 
 * @class JidUtils
 * @example
 * const unformattedJid = JidUtils.unformatJid("5518999999999@satisfies.whatsapp.net");
 * console.log(unformattedJid); // 5518999999999
 */
class JidUtils {
  //  ****************
  //  ** Attributes **
  //  ****************
  static patternsToClearJid = [/[^0-9]/g, /[^0-9,]/g, /^155/];
  


  //  *************
  //  ** Methods **
  //  *************

  /**
   * 
   * @param {string} jid - The JID to be cleared.
   * @param {boolean} shouldConsiderCommas - True to consider commas and false to remove commas.
   * @returns {string} The cleared JID: only numbers (shouldConsiderCommas: false); only numbers and commas (shouldConsiderCommas: true).
   */
  static clearJid(jid, shouldConsiderCommas = false) {
    const pattern = shouldConsiderCommas ? this.patternsToClearJid[1] : this.patternsToClearJid[0];
    return jid.trim().replace(pattern, "");
  }

  /**
   * 
   * @param {string} jid - The JID to be unformatted.
   * @returns {string} The unformatted JID (only numbers) if JID is string, "-" otherwise.
   */
  static unformatJid(jid) {
    if (typeof jid !== "string" || !jid.trim()) return "-";
    return jid.split("@")[0].split(":")[0];
  }

  /**
   * 
   * @param {string} jid - The JID to be cleared and unformatted.
   * @returns {string | *} The cleared and unformatted JID is JID is string, the raw JID otherwise.
   */
  static unformatAndClearJid(jid) {
    return typeof jid === "string" ? this.clearJid(this.unformatJid(jid)) : jid;
  }

  /**
   * 
   * @param {string} jid - The contact's JID.
   * @param {boolean} isGroup - Whether the JID is from a group or not.
   * @returns {string} The JID formatted to be copied to the clipboard:
   *  \ if contact is group, the raw JID is returned;
   *  \ removed "55" if JID starts with it;
   *  \ added the nine digit (9) if JID length is ten characters and is not landline (number part doesn't start with 2, 3, 4 or 5).
   */
  static formatJidToClipboard(jid, isGroup) {
    if (isGroup) return this.clearJid(jid, false);

    let cleanJid = this.clearJid(jid, false);

    if (cleanJid.startsWith("55")) {
      cleanJid = cleanJid.slice(2);
    }
    
    if (cleanJid.length === 10 && !["2", "3", "4", "5"].includes(cleanJid[2])) {
      cleanJid = `${cleanJid.slice(0, 2)}9${cleanJid.slice(2)}`;
    }

    return cleanJid;
  }
  
  /**
   * 
   * @param {string} jid - The JID to be validated for dispatch.
   * @returns {boolean} True if JID is valid (starts with "55" AND contains 12 or 13 characters), false otherwise.
   */
  static validJidToDispatch(jid) {
    if (!jid) return false;
    return jid.startsWith("55") && [12, 13].includes(jid.length);
  }

  /**
   * 
   * @param {string} jid - The JID to be formatted.
   * @param {boolean} isGroup - Whether the JID from is group or not.
   * @param {boolean} isLid - Whether the JID is lid or not.
   * @returns {string} The formatted JID accordingly to its type (phone number, lid or group).
   */
  static formatJid(jid, isGroup, isLid) {
    if (isGroup) return `${jid}${DefaultValuesUtils.JIDS.group}`;
    else if (isLid) return `${jid}${DefaultValuesUtils.JIDS.lid}`;
    else return `${jid}${DefaultValuesUtils.JIDS.chat}`;
  }

  /**
   * 
   * @param {string} jid - The JID to be formated as VCard.
   * @returns {string} The formatted JID.
   */
  static formatJidToVCards(jid) {
    const formattedJid = this.unformatJid(jid);

    return formattedJid.startsWith("155") && formattedJid.length > 11
      ? formattedJid.replace(this.patternsToClearJid[2], "55")
      : formattedJid;
  }

  /**
   * 
   * @param {Contact} contact - The contact have its phone number and/or lid shown.
   * @returns {string} if:
   *   \ phone number and lid exist: returns a string containing the raw phone number and the formatted lid;
   *   \ only phone number exists: returns the raw phone number;
   *   \ only lid exists: returns the formatted lid.
   */
  static handleShowJid(contact) {
    if (!contact) return "";

    const phoneNumber = contact.number;
    const lid = contact.lid;

    if (!phoneNumber && !lid) return "";

    return phoneNumber && lid
      ? `${phoneNumber} | ${this.formatJid(lid, false, true)}`
      : phoneNumber || JidUtils.formatJid(lid, false, true);
  };
}

export default JidUtils;
